<template>
<div>
  <div class="container">
    <h1>CDM Probe Page</h1>
    <p>
      This page allows you to check which CDMs and with which configuration
      flags are supported by your browser.
    </p>

    <table>
      <thead>
        <tr>
          <th rowspan="2" title="Content Decryption Module">CDM</th>
          <th rowspan="2">Key System</th>
          <th colspan="3" scope="colgroup">Session Type: Temporary</th>
          <th colspan="3" scope="colgroup">Session Type: Persistent License</th>
        </tr>
        <tr>
          <th>Supported</th>
          <th>Distinctive Identifier</th>
          <th>Persistent State</th>
          <th>Supported</th>
          <th>Distinctive Identifier</th>
          <th>Persistent State</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in results" :key="index">
          <td>{{ item.name }}</td>
          <td>{{ item.keySystem }}</td>
          <td>{{ item.temporarySession.supported ? "✅" : "❌"}}</td>
          <td>{{ item.temporarySession.supported ? item.temporarySession.distinctiveIdentifiers : '—' }}</td>
          <td>{{ item.temporarySession.supported ? item.temporarySession.persistentState : '—' }}</td>
          <td>{{ item.persistentSession.supported ? "✅" : "❌"}}</td>
          <td>{{ item.persistentSession.supported ? item.persistentSession.distinctiveIdentifiers : '—' }}</td>
          <td>{{ item.persistentSession.supported ? item.persistentSession.persistentState : '—' }}</td>
        </tr>
      </tbody>
    </table>

    <div class="version">
      App Version: {{ appVersion }}<br>
      User Agent: {{ userAgent }}<br>
    </div>
  </div>
</div>
</template>

<script>
/**
 * Helper function to check if a specific CDM configuration is supported.
 * @param {string} keySystem The key system string (e.g., 'com.widevine.alpha')
 * @param {object} cdmConfig The configuration object for requestMediaKeySystemAccess
 * @returns {Promise<boolean>} True if supported, false otherwise.
 */
async function isConfigSupported (keySystem, cdmConfig) {
  try {
    // Attempt to request MediaKeySystemAccess with the given config.
    // If this promise resolves, it means the config is supported.
    await navigator.requestMediaKeySystemAccess(keySystem, cdmConfig);
    return true;
  } catch (e) {
    // If the promise rejects, the config is not supported.
    return false;
  }
}

/**
 * Checks a single CDM and session type for support, including different
 * combinations of distinctive identifiers and persistent state.
 * @param {object} config The base configuration object for the CDM.
 * @param {string} sessionType The session type to check ('temporary' or 'persistent-license').
 * @returns {Promise<object>} An object with support booleans.
 */
async function checkOneCdmSupport (config, sessionType) {
  const videoCodec = 'video/mp4;codecs="avc1.42E01E"';
  const audioCodec = 'audio/mp4;codecs="mp4a.40.2"';

  // Base configuration with no specific identifier/state requirements
  const baseCdmConfig = [{
    initDataTypes: [config.initDataType],
    videoCapabilities: [{ contentType: videoCodec, robustness: config.videoRobustness }],
    audioCapabilities: [{ contentType: audioCodec, robustness: config.audioRobustness }],
    sessionTypes: [sessionType],
  }];

  // Check general support
  const supported = await isConfigSupported(config.keySystem, baseCdmConfig);

  // Check distinctive identifier support.
  // This logic is conditional on the session type.
  let distinctiveIdentifiers;
  if (sessionType === 'temporary') {
    // A temporary session, may have a distinctive identifier.
    distinctiveIdentifiers = 'not-allowed';
    const requiredIdConfig = [{ ...baseCdmConfig[0], distinctiveIdentifier: 'required' }];
    const optionalIdConfig = [{ ...baseCdmConfig[0], distinctiveIdentifier: 'optional' }];
    if (await isConfigSupported(config.keySystem, requiredIdConfig)) {
      distinctiveIdentifiers = 'required';
    } else if (await isConfigSupported(config.keySystem, optionalIdConfig)) {
      distinctiveIdentifiers = 'optional';
    }
  } else {
    // For persistent license sessions, we probe the browser for support.
    distinctiveIdentifiers = 'not-allowed';
    const requiredIdConfig = [{ ...baseCdmConfig[0], distinctiveIdentifier: 'required' }];
    const optionalIdConfig = [{ ...baseCdmConfig[0], distinctiveIdentifier: 'optional' }];
    if (await isConfigSupported(config.keySystem, requiredIdConfig)) {
      distinctiveIdentifiers = 'required';
    } else if (await isConfigSupported(config.keySystem, optionalIdConfig)) {
      distinctiveIdentifiers = 'optional';
    }
  }

  // Check persistent state support.
  // This logic is conditional on the session type.
  let persistentState;
  if (sessionType === 'temporary') {
    // A temporary session, by definition, cannot have a persistent state.
    persistentState = 'not-allowed';
  } else {
    // For persistent license sessions, we probe the browser for support.
    persistentState = 'not-allowed';
    const requiredPsConfig = [{ ...baseCdmConfig[0], persistentState: 'required' }];
    const optionalPsConfig = [{ ...baseCdmConfig[0], persistentState: 'optional' }];
    if (await isConfigSupported(config.keySystem, requiredPsConfig)) {
      persistentState = 'required';
    } else if (await isConfigSupported(config.keySystem, optionalPsConfig)) {
      persistentState = 'optional';
    }
  }

  return {
    supported,
    distinctiveIdentifiers,
    persistentState,
  };
}

/**
 * Checks all predefined CDMs for support.
 * @returns {Promise<Array>} A list of results for each CDM.
 */
async function checkAllCdmSupport () {
  const resultList = [];
  const configs = [
    { name: 'Clear Key', keySystem: 'org.w3.clearkey', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'cenc' },
    { name: 'FairPlay (SKD)', keySystem: 'com.apple.fps', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'skd' },
    { name: 'FairPlay (SINF)', keySystem: 'com.apple.fps', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'sinf' },
    { name: 'Widevine L3 (SW_SECURE_CRYPTO)', keySystem: 'com.widevine.alpha', videoRobustness: 'SW_SECURE_CRYPTO', audioRobustness: 'SW_SECURE_CRYPTO', initDataType: 'cenc' },
    { name: 'Widevine L2 (HW_SECURE_CRYPTO)', keySystem: 'com.widevine.alpha', videoRobustness: 'HW_SECURE_CRYPTO', audioRobustness: 'HW_SECURE_CRYPTO', initDataType: 'cenc' },
    { name: 'Widevine L1 (HW_SECURE_DECODE)', keySystem: 'com.widevine.alpha', videoRobustness: 'HW_SECURE_DECODE', audioRobustness: 'HW_SECURE_DECODE', initDataType: 'cenc' },
    { name: 'PlayReady SW DRM', keySystem: 'com.microsoft.playready.recommendation', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'cenc' },
    { name: 'PlayReady SW DRM (legacy)', keySystem: 'com.microsoft.playready', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'cenc' },
    { name: 'PlayReady HW DRM', keySystem: 'com.microsoft.playready.recommendation.3000', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'cenc' },
    { name: 'PlayReady HW DRM (legacy)', keySystem: 'com.microsoft.playready.hardware', videoRobustness: undefined, audioRobustness: undefined, initDataType: 'cenc' },
  ];

  for (const config of configs) {
    console.log(`Checking ${config.name}...`);
    const temporaryResults = await checkOneCdmSupport(config, 'temporary');
    const persistentResults = await checkOneCdmSupport(config, 'persistent-license');
    resultList.push({
      ...config,
      temporarySession: temporaryResults,
      persistentSession: persistentResults,
    });
  }

  return resultList;
}

export default {
  name: 'page-cdm',
  data () {
    return {
      appVersion: navigator.appVersion,
      userAgent: navigator.userAgent,
      results: [],
    };
  },
  async mounted () {
    this.results = await checkAllCdmSupport();
  }
};
</script>

<style lang="css" scoped>
.container {
  width: 90%;
  font-size: 0.8em;
  margin: 0 auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1em;
  background: white;
}

tbody tr:hover {
  background-color: #f0f0f0;
}

th[title] {
  cursor: help;
}

.version {
  margin-top: 1em;
  font-size: 0.8em;
  color: #555;
}

.small {
  font-size: 0.7em;
}

th {
  color: white;
  border: 1px solid white;
  background-color: rgb(194, 54, 51);
  padding: 0.5em;
  text-align: center;
}

td {
  border: 1px solid #ccc;
  padding: 0.5em;
  text-align: center;
}
</style>
